import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Share2, Download, Flame, Trophy, TrendingDown, Calendar } from "lucide-react";
import { toast } from "sonner";

export default function Share() {
  const [stats, setStats] = useState(null);
  const [imgUrl, setImgUrl] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/streak");
        setStats(r.data);
      } catch {}
    })();
    const token = localStorage.getItem("fp_token");
    const base = process.env.REACT_APP_BACKEND_URL;
    setImgUrl(`${base}/api/share-card.png?auth=${encodeURIComponent(token)}&v=${Date.now()}`);
  }, []);

  const refreshCard = () => {
    const token = localStorage.getItem("fp_token");
    const base = process.env.REACT_APP_BACKEND_URL;
    setImgUrl(`${base}/api/share-card.png?auth=${encodeURIComponent(token)}&v=${Date.now()}`);
    toast.success("Card refreshed");
  };

  const downloadCard = async () => {
    try {
      const res = await fetch(imgUrl);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "ripple-effect-streak.png";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    }
  };

  const sharePng = async () => {
    try {
      const res = await fetch(imgUrl);
      const blob = await res.blob();
      const file = new File([blob], "fitprogress-streak.png", { type: "image/png" });
      const shareData = {
        title: "My Ripple Effect streak",
        text: `${stats?.current_streak ?? 0}-day streak on Ripple Effect 🔥`,
        files: [file],
      };
      if (navigator.canShare && navigator.canShare(shareData)) {
        await navigator.share(shareData);
      } else if (navigator.share) {
        await navigator.share({
          title: shareData.title,
          text: shareData.text,
          url: window.location.origin,
        });
      } else {
        await navigator.clipboard.writeText(
          `${shareData.text} ${window.location.origin}`
        );
        toast.success("Caption copied — paste anywhere");
      }
    } catch (e) {
      // user cancelled or share unsupported
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="overline text-blaze">/ Share</div>
        <h1 className="font-heading text-5xl uppercase mt-1">Flex the work.</h1>
        <p className="text-zinc-400 text-sm mt-2">Auto-generated share card with your streak, top PR, and weight change. Send it anywhere.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="stat-tile" data-testid="share-streak">
          <div className="overline flex items-center gap-2"><Flame className="h-3 w-3 text-blaze" /> Streak</div>
          <div className="font-data text-3xl">{stats?.current_streak ?? 0}<span className="text-xs text-zinc-500 ml-1">days</span></div>
        </div>
        <div className="stat-tile">
          <div className="overline flex items-center gap-2"><Calendar className="h-3 w-3" /> Best run</div>
          <div className="font-data text-3xl">{stats?.longest_streak ?? 0}<span className="text-xs text-zinc-500 ml-1">days</span></div>
        </div>
        <div className="stat-tile">
          <div className="overline flex items-center gap-2"><Trophy className="h-3 w-3 text-blaze" /> Top PR</div>
          <div className="font-data text-3xl">{stats?.top_pr_kg ?? "—"} <span className="text-xs text-zinc-500">kg</span></div>
          {stats?.top_pr_name && <div className="text-[10px] text-zinc-500 uppercase tracking-widest mt-1 truncate">{stats.top_pr_name}</div>}
        </div>
        <div className="stat-tile">
          <div className="overline flex items-center gap-2"><TrendingDown className="h-3 w-3" /> Weight Δ</div>
          <div className="font-data text-3xl">
            {stats?.weight_delta_kg != null ? `${stats.weight_delta_kg > 0 ? "+" : ""}${stats.weight_delta_kg}` : "—"}
            <span className="text-xs text-zinc-500 ml-1">kg</span>
          </div>
        </div>
      </div>

      <div className="obs-card">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div>
            <div className="overline">Your share card</div>
            <h3 className="font-heading text-2xl uppercase">1200 × 630 · ready to post</h3>
          </div>
          <div className="flex gap-2">
            <button onClick={refreshCard} data-testid="share-refresh" className="ghost-btn px-4 py-2 text-xs">Refresh</button>
            <button onClick={downloadCard} data-testid="share-download" className="ghost-btn px-4 py-2 text-xs inline-flex items-center gap-2">
              <Download className="h-3 w-3" /> Download
            </button>
            <button onClick={sharePng} data-testid="share-button" className="blaze-btn px-4 py-2 text-xs inline-flex items-center gap-2">
              <Share2 className="h-3 w-3" /> Share
            </button>
          </div>
        </div>
        {imgUrl ? (
          <div className="border border-obsidian-600 rounded-sm overflow-hidden bg-obsidian-900">
            <img src={imgUrl} data-testid="share-card-img" alt="share card" className="w-full block" />
          </div>
        ) : (
          <div className="h-64 grid place-items-center text-zinc-500 uppercase tracking-widest text-sm">
            Loading card...
          </div>
        )}
      </div>
    </div>
  );
}
